// The Bosla admin's words (UC-20 to UC-24, UC-64). English only: the admin is for Bosla's own
// staff and never offers a language, so it has no Arabic twin in i18n. The
// words are the design's, from the Claude Design project's Admin page.

import type { ThemeChoice } from '../display.svelte';

export const copy = {
	title: 'Bosla admin',
	signInLine: 'Sign in with your Bosla staff Google account.',
	signIn: 'Sign in with Google',
	signInOpening: 'Opening Google…',
	sessionEnded: 'Your admin session ended. Sign in again.',
	signInUnfinished: 'Google sign-in did not finish. Try again.',
	refused: 'This Google account is not a Bosla admin.',
	googleId: 'Google id',
	copy: 'Copy',
	copied: 'Copied',
	refusedHow: 'To let it in, add this id to the admin list in Pulumi.',
	signedInAs: 'Signed in as',
	signOut: 'Sign out',
	loading: 'Loading the admin',
	overview: 'Overview',
	accounts: 'Accounts',
	subscriptions: 'Subscriptions',
	retry: 'Retry',

	// A2 Overview (UC-21).
	overviewLoading: 'Loading the overview',
	overviewFailed: 'The overview could not be loaded.',
	noRestaurants: 'No restaurants yet.',
	restaurants: 'Restaurants',
	restaurantsNote: 'Every account, suspended ones included.',
	fresh: 'New',
	freshNote: 'Created in the last 7 days.',
	selling: 'Selling',
	sellingValue: (selling: number, of: number) => `${selling} of ${of}`,
	sellingNote: 'Took an order in the last 7 days.',
	countsNote: 'Junk sign-ups count in Restaurants and New, so neither one means customers.',
	ordersPerDay: 'Orders per day',
	ordersPerDayNote: "Last 30 days · each order on its shop's trading day",
	dayColumn: 'Day',
	ordersColumn: 'Orders',
	todaySoFar: 'today, so far',
	loadNote:
		'This is load on Bosla, not how restaurants do. One large restaurant can move it alone.',
	tillsPerBuild: 'Tills per app build',
	tillsPerBuildNote: 'Paired tills, by their last heartbeat · lowest build first',
	buildColumn: 'App build',
	tillsCountColumn: 'Tills',
	appBuild: (build: number) => `Build ${build}`,
	buildNotReported: 'Not reported yet',
	noPairedTills: 'No paired tills yet.',
	buildsNote:
		'A till that is switched off keeps the build it last reported, so this counts what tills last said, not what runs now.',

	// A3 Accounts (UC-21).
	accountsLoading: 'Loading accounts',
	accountsFailed: 'The accounts could not be loaded.',
	nobodyYet: 'Nobody has signed up yet.',
	search: "Search by owner's email or restaurant name",
	noMatch: 'Nobody matches that search.',
	accountsNote: 'Newest sign-up first · 50 at a time',
	matchesNote: 'Matches, newest sign-up first · 50 at a time',
	restaurantOwnerColumn: 'Restaurant · owner',
	signedUpColumn: 'Signed up',
	stateColumn: 'State',
	shopsColumn: 'Shops',
	tillsColumn: 'Tills',
	lastOrderColumn: 'Last order',
	activeUntilColumn: 'Active until',
	ending: 'Ending',
	ended: 'Ended',
	noRestaurant: 'No restaurant yet',
	active: 'Active',
	noOrders: 'No orders yet',
	more: 'More',
	moreLoading: 'Loading…',
	suspended: (on: string) => `Suspended ${on}`,
	open: 'Open',
	setupNote:
		'Shops and tills show who stopped during setup. Last order is only a date: a shop closed for a holiday looks the same as one that stopped. Active until shows whom to contact about renewing; every date there is one Bosla set, not anything the restaurant did. Ending and Ended are a warning only: nothing stops on the day.',

	// A4 An account (UC-23).
	account: 'Account',
	accountLoading: 'Loading the account',
	accountFailed: 'This account could not be loaded.',
	owner: 'Owner',
	signedUp: 'Signed up',
	shops: 'Shops',
	tills: 'Tills',
	lastOrder: 'Last order',
	suspend: 'Suspend',
	resume: 'Resume',
	no: 'No',
	resuming: 'Resuming…',
	resumeQuestion: (name: string) => `Resume ${name}? The owner and its tills work again at once.`,
	suspendNote: 'Suspending stops the owner and every till at once. There is no Undo.',
	resumeNote: 'Resume reverses a suspension. No reason is asked.',
	changedTo: (state: string) => `Someone changed this account. It is now ${state}.`,
	history: 'History',
	historyNote: "Newest first · only Bosla's admins see a reason",
	whenColumn: 'When',
	adminColumn: 'Admin',
	actionColumn: 'Action',
	reasonColumn: 'Reason',
	neverSuspended: 'Never suspended.',
	actionSuspended: 'Suspended',
	actionResumed: 'Resumed',
	flashSuspended: 'Suspended.',
	flashResumed: 'Resumed.',
	suspendTitle: (name: string) => `Suspend ${name}`,
	suspendEffect: 'The owner and every till stop at once, even in the middle of a sale.',
	suspendDrawer: 'A cash sale just taken may not open its drawer.',
	reason: 'Reason',
	reasonPlaceholder: 'e.g. Spam sign-up, no real restaurant',
	reasonRequired: "Required. Only Bosla's admins see it, never the owner or the cashiers.",
	reasonCount: (n: number) => `${n} of 500 · only Bosla's admins see it`,
	noUndo: 'There is no Undo. Resume reverses a suspension.',
	confirm: 'Confirm',
	suspending: 'Suspending…',
	close: 'Close',

	// The subscription on A4 (UC-24).
	subscription: 'Subscription',
	noEndDate: 'No end date',
	endsLine: (on: string, daysLeft: number) =>
		daysLeft < 0
			? `Ended ${on}`
			: daysLeft === 0
				? `Ends ${on}, today`
				: daysLeft <= 14
					? `Ends ${on}, in ${daysLeft === 1 ? '1 day' : `${daysLeft} days`}`
					: `Ends ${on}`,
	subscriptionEnd: 'Subscription end',
	subscriptionEndNote:
		'Only Bosla sets it. The owner sees a warning from 14 days before, and after.',
	endSheetTitle: (name: string) => `Subscription end · ${name}`,
	endsOn: 'Ends on',
	endsOnNote: "YYYY-MM-DD · runs through that whole day, in the restaurant's time zone",
	invalidDate: 'Pick a real date.',
	effectFrom: (on: string) => `The owner sees a warning from ${on}.`,
	effectNow: 'The owner sees a warning now.',
	effectEnded: 'The owner sees that it ended.',
	currentEnds: (line: string) =>
		`Now: ${line}. No end date clears it, and the owner's warning goes away.`,
	currentNone: 'Now: no end date. The owner sees no warning.',
	actionSubscriptionSet: (on: string) => `Subscription end set to ${on}`,
	actionSubscriptionCleared: 'Subscription end cleared',

	// A visit to the owner's dashboard on A4 (UC-64).
	openDashboard: 'Open owner dashboard',
	openingDashboard: 'Opening…',
	openDashboardNote:
		'Opens in a new tab for one hour. Use a private window if you are signed in to your own restaurant.',
	visitSuspended: 'This restaurant is suspended',
	visitGone: 'This restaurant no longer exists',
	visitBlocked:
		'The browser did not open the new tab. Open it here within a minute, or open the dashboard again.',
	actionVisitOpened: 'Opened a visit link',
	visits: 'Visits',
	visitsNote: 'Newest first · open one to see what it changed',
	timeColumn: 'Time',
	endColumn: 'End',
	noVisits: 'No visits yet.',
	visitOpenUntil: (at: string) => `Open until ${at}`,
	visitLeft: 'Left',
	visitReset: 'Ended by a password reset',
	visitExpired: 'Ended at its hour',
	visitRemoved: 'Ended: admin removed',
	actionsLoading: 'Loading what this visit changed',
	actionsFailed: 'What this visit changed could not be loaded.',
	noActions: 'This visit changed nothing.',
	visitChanges: (at: string, by: string) => `Changes · ${at} · ${by}`,
	visitChangesNote: 'Oldest first · a method, a path and a status, never what was sent',
	statusColumn: 'Status',
	startedGoogleLink: 'Started adding a Google account',

	// A6 Subscriptions (UC-24).
	subscriptionsLoading: 'Loading subscriptions',
	subscriptionsFailed: 'The subscriptions could not be loaded.',
	noSubscriptions: 'No subscription ends in the next 14 days, and none has ended.',
	toContact: 'To contact about renewing',
	subscriptionsNote: 'Earliest end date first · ending within 14 days, or ended',
	endsColumn: 'Ends',
	subscriptionsFootnote:
		'Every date here is one Bosla set, not anything the restaurant did. A restaurant with no end date never appears.',
	leftLine: (daysLeft: number) => {
		const n = Math.abs(daysLeft);
		const days = n === 1 ? '1 day' : `${n} days`;
		return daysLeft < 0 ? `Ended ${days} ago` : daysLeft === 0 ? 'Ends today' : `Ends in ${days}`;
	},

	// A5 Support contact (UC-22).
	supportContact: 'Support contact',
	contactLoading: 'Loading the support contact',
	contactFailed: 'The support contact could not be loaded.',
	contactCard: 'How a suspended restaurant reaches Bosla',
	email: 'Email',
	emailNote: 'Set by the domain.',
	whatsapp: 'WhatsApp number',
	whatsappNote: 'With its country code. Leave it empty to remove the number.',
	whatsappInvalid: 'Type the number with its country code, like +201001234567.',
	noWhatsapp: 'No WhatsApp number. Suspended restaurants see the email only.',
	changed: (on: string, by: string) => `Changed ${on} by ${by}`,
	saveFailed: 'That did not go through. Try again.',
	save: 'Save',
	saving: 'Saving…',
	saved: 'Saved.',
	previewCard: 'What a suspended restaurant reads',
	previewNote: 'As typed, before Save',
	previewOwner: 'The owner, on the dashboard',
	previewOwnerTitle: "Bosla has suspended your restaurant's account.",
	previewOwnerTills: 'Your tills have stopped too.',
	previewOwnerContact: 'Contact Bosla:',
	previewTill: 'The cashiers, on the till (plain text)',
	previewTillTitle: "Bosla has suspended this restaurant's account. Tell the owner.",
	previewTillContact: 'Bosla support:',
	whatsappLine: 'WhatsApp',

	theme: (choice: ThemeChoice) =>
		`Theme · ${{ light: 'Light', dark: 'Dark', system: 'Follow system' }[choice]}`,
};
